import { apiClient } from '@leap4/frontend';

import { reportingBaseUrl } from '../../../config/generals';
import { QueryStatusResponse } from './hooks/useAsyncQuery';
import { IReport } from './report.interface';
import { IPaginationModel } from './reportContext';

export async function pollQueryUntilDone<T = any>(
  queryId: string,
  maxWaitMs = 30 * 60 * 1000
): Promise<QueryStatusResponse<T>> {
  const POLL_MS = 2000;
  const deadline = Date.now() + maxWaitMs;
  while (Date.now() < deadline) {
    const res = await apiClient.get<QueryStatusResponse<T>>(
      `${reportingBaseUrl}/queries/${queryId}`
    );
    if (res.data.status !== 'running') return res.data;
    await new Promise((r) => setTimeout(r, POLL_MS));
  }
  throw new Error(`Query polling timed out after ${Math.round(maxWaitMs / 60000)} minutes`);
}

export const getReportQueryFingerprint = (
  reportData: IReport,
  buildQueryBody: (
    report: IReport,
    pagination: IPaginationModel,
    useCursor?: boolean
  ) => Record<string, any>
): string => {
  const body = buildQueryBody(reportData, { page: 0, pageSize: 1, cursor: null }, false);
  delete body.page;
  delete body.pageSize;
  delete body.cursor;
  return JSON.stringify(body);
};

export const cloneReportConfig = (reportData: IReport): IReport => ({
  ...reportData,
  dimensions: reportData.dimensions.map((d) => ({ ...d })),
  metrics: reportData.metrics.map((m) => ({ ...m })),
  customLabels: reportData.customLabels.map((c) => ({ ...c })),
  filters: reportData.filters.map((f) => ({
    ...f,
    specTarget: f.specTarget.map((s) => ({ ...s })),
  })),
  // Reports saved before constraints existed have no such field.
  constraints: (reportData.constraints || []).map((c) => ({ ...c })),
});
